import { useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { Box, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

const OTPBox = styled(TextField)(() => ({
  width: 56,
  '& .MuiOutlinedInput-root': {
    height: 56,
    padding: 0,
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    '& input': {
      textAlign: 'center',
      fontSize: '1.5rem',
      fontWeight: 600,
      padding: 0,
      color: '#111827'
    },
    '& fieldset': {
      borderColor: '#E5E7EB'
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB'
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#4F46E5',
        borderWidth: '2px'
      }
    }
  }
}));

interface OTPInputProps {
  length?: number;
  value: string[];
  onChange: (value: string[]) => void;
}

const OTPInput = ({ length = 6, value, onChange }: OTPInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    
    // Only allow numbers
    if (val && !/^\d$/.test(val)) return;

    const newValue = [...value];
    newValue[index] = val;
    onChange(newValue);

    // Auto-focus next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length);
    
    if (!/^\d+$/.test(pastedData)) return;

    const newValue = pastedData.split('');
    onChange([...newValue, ...new Array(length - newValue.length).fill('')]);
    
    // Focus last filled input or last input
    const focusIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
      {Array.from({ length }).map((_, index) => (
        <OTPBox
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={value[index] || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e)}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            'aria-label': `OTP digit ${index + 1}`
          }}
        />
      ))}
    </Box>
  );
};

export default OTPInput;

